import React, { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import {
    Box, Button, Typography, Stack, Card, CardContent, Divider, useTheme, Fade, Zoom, Container, TextField, InputAdornment,
    IconButton, Tooltip, CircularProgress, Breadcrumbs, Link,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SearchIcon from '@mui/icons-material/Search';
import axiosInstance from '../../lib/axios';
import { toast, ToastContainer } from 'react-toastify';
import { useReactToPrint } from 'react-to-print';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../redux/propertySlice';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import HomeIcon from '@mui/icons-material/Home';
import ExcelJS from 'exceljs';

const ReceiptReport = () => {
    const theme = useTheme();
    const [fromDate, setFromDate] = useState(dayjs().subtract(1, 'month'));
    const [toDate, setToDate] = useState(dayjs());
    const [records, setRecords] = useState([]);
    const [filteredRecords, setFilteredRecords] = useState([]);
    const [searchText, setSearchText] = useState('');
    const contentRef = useRef();
    const globalProperty = useSelector(selectGlobalProperty);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const handlePrint = useReactToPrint({
        contentRef,
        documentTitle: `Receipt Report`,
        pageStyle: `
            @page { 
                size: A4 portrait; 
                margin: 1.2cm;
                @top-center { content: "Receipt Report"; font-weight: bold; font-size: 24px; color: #635bff }
                @bottom-center { content: "Page " counter(page) " of " counter(pages); font-size: 10px; }
            }
            @media print {
                body { 
                    font-family: Arial, sans-serif; 
                    margin: 0; 
                    -webkit-print-color-adjust: exact; 
                    print-color-adjust: exact; 
                }
                .no-print { display: none !important; }
                .print-header { 
                    text-align: center; 
                    margin-bottom: 16px; 
                    page-break-after: avoid;
                }
                .print-title { 
                    font-size: 18px; 
                    font-weight: bold; 
                    margin: 4px 0; 
                    color: #635bff;
                }
                .print-subtitle { 
                    font-size: 12px; 
                    color: #555; 
                    margin: 3px 0;
                }
                table { 
                    width: 100%; 
                    border-collapse: collapse; 
                    font-size: 11px; 
                    page-break-inside: avoid;
                }
                th { 
                    background: #635bff !important; 
                    color: white !important; 
                    padding: 3px 6px; 
                    font-weight: bold;
                    text-align: left;
                }
                td { 
                    padding: 5px 8px; 
                    border-bottom: 1px solid #eee;
                }
                tr:nth-child(even) td { 
                    background-color: #f9f9f9; 
                }
                .total-row td { 
                    background: #e8f5e9 !important; 
                    font-weight: bold; 
                    color: #1b5e20;
                }
                .net-amount { 
                    font-weight: 700; 
                    color: #e91e63; 
                }
            }
        `,
    });

    const fetchRecords = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.post('TrnRctVchr/GetTrnRctVchrFilterDashBoard', {
                PageNumber: 1,
                PageSize: 1,
                SearchText: "",
                PropId: globalProperty,
                SearchFrom: fromDate.format('YYYY-MM-DD'),
                SearchTo: toDate.format('YYYY-MM-DD'),
                Flag: "RctRpt"
            });
            if (response.data.Status === 0) {
                setRecords(response.data.Data || []);
                setFilteredRecords(response.data.Data || []);
                if (response.data.Data.length === 0) {
                    toast.info('No records found for the selected date range.');
                }
            } else {
                toast.info(response.data.Message || 'No records found.');
                setRecords([]);
            }
        } catch (error) {
            toast.error('Error fetching records from the server.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRecords();
    }, [globalProperty]);

    useEffect(() => {
        const searchWords = searchText.trim().toLowerCase().split(/\s+/);
        const filtered = records.filter((rec) => {
            const residentWords = rec.ResidentName?.toLowerCase().split(/\s+/) || [];
            const propWords = rec.PropName?.toLowerCase().split(/\s+/) || [];
            const advFor = rec.Remark?.toLowerCase().split(/\s+/) || [];
            const RecpNo = rec.RecNo?.toLowerCase().split(/\s+/) || [];
            const advNo = rec.PytAdvNo?.toLowerCase().split(/\s+/) || [];
            const mobNo = rec.MobileNo?.toLowerCase().split(/\s+/) || [];
            const book = rec.ChequeNo?.toLowerCase().split(/\s+/) || [];
            const recDate = dayjs(rec.RecDt).format('DD-MM-YYYY').toLowerCase();
            const chqDate = dayjs(rec.ChqDt).format('DD-MM-YYYY').toLowerCase();
            const amount = rec.RcdAmt?.toString();

            return searchWords.every((word) =>
                residentWords.some((w) => w.includes(word)) ||
                propWords.some((w) => w.includes(word)) ||
                advFor.some((w) => w.includes(word)) ||
                RecpNo.some((w) => w.includes(word)) ||
                advNo.some((w) => w.includes(word)) ||
                mobNo.some((w) => w.includes(word)) ||
                book.some((w) => w.includes(word)) ||
                recDate.includes(word) ||
                chqDate.includes(word) ||
                amount.includes(word)
            );
        });
        setFilteredRecords(filtered);
    }, [searchText, records]);

    const handleLoadRecords = () => {
        fetchRecords();
    };

    const total = filteredRecords.reduce((acc, r) => acc + Number(r.RcdAmt || 0), 0);

    const handleExportToExcel = () => {
        if (filteredRecords.length === 0) {
            toast.info("No records to export");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Receipt Report');

        // ==================== Light Blue Border Definition ====================
        const lightBlueBorder = {
            top: { style: 'thin', color: { argb: 'FF81C4FF' } },
            left: { style: 'thin', color: { argb: 'FF81C4FF' } },
            bottom: { style: 'thin', color: { argb: 'FF81C4FF' } },
            right: { style: 'thin', color: { argb: 'FF81C4FF' } }
        };

        // ==================== TITLE ====================
        worksheet.mergeCells('A1:K1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = `Receipt Report | ${fromDate.format('DD-MMM-YYYY')} to ${toDate.format('DD-MMM-YYYY')}`;
        titleCell.font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
        titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '635BFF' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        titleCell.border = lightBlueBorder;   // Added

        // ==================== HEADER ROW ====================
        const headers = [
            'Property', 'Receipt No', 'AdvNo', 'AdvFor', 'Receipt Date', 'Mobile No',
            'Resident', 'Payment Type', 'TrjNo', 'Trj Date', 'Amount (₹)'
        ];
        const headerRow = worksheet.addRow(headers);

        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '635BFF' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = lightBlueBorder;        // Changed to light blue
        });

        headerRow.height = 22;

        // ==================== DATA ROWS ====================
        filteredRecords.forEach((rec, index) => {
            const row = worksheet.addRow([
                rec.PropName || 'ABC',
                rec.RecNo || '',
                rec.PytAdvNo || '',
                rec.Remark || '',
                rec.RecDt ? dayjs(rec.RecDt).format('DD-MM-YYYY') : '',
                rec.MobileNo || '',
                rec.ResidentName || '',
                rec.Pyttype || '',
                rec.ChequeNo || '',
                rec.ChqDt ? dayjs(rec.ChqDt).format('DD-MM-YYYY') : '',
                Number(rec.RcdAmt || 0)
            ]);

            // Alternate row color + Light Blue Border
            row.eachCell((cell) => {
                if (index % 2 === 0) {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
                }
                cell.border = lightBlueBorder;
            });

            // Payment type color
            const paymentCell = row.getCell(7);
            if ((rec.Pyttype || '').toLowerCase() === 'online') {
                paymentCell.font = { color: { argb: '008000' }, bold: true };
            } else if ((rec.Pyttype || '').toLowerCase() === 'cash') {
                paymentCell.font = { color: { argb: 'D35400' }, bold: true };
            }

            // Amount formatting
            row.getCell(9).numFmt = '#,##0';
            row.getCell(9).alignment = { horizontal: 'right' };
        });

        // ==================== TOTAL ROW ====================
        const total = filteredRecords.reduce((sum, r) => sum + Number(r.RcdAmt || 0), 0);
        const totalRow = worksheet.addRow(['TOTAL', '', '', '', '', '', '', '', '', '', total]);

        totalRow.eachCell((cell, colNumber) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '635BFF' } };
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.border = lightBlueBorder;        // Added light blue border
            if (colNumber === 9) {
                cell.numFmt = '#,##0';
                cell.alignment = { horizontal: 'right' };
            }
        });

        totalRow.height = 22;

        // ==================== SUMMARY ROWS ====================
        const summaryStart = worksheet.lastRow.number + 2;

        const totalRecords = filteredRecords.length;
        const onlineAmt = filteredRecords
            .filter(r => String(r.Pyttype || '').toLowerCase() === 'online')
            .reduce((sum, r) => sum + Number(r.RcdAmt || 0), 0);
        const cashAmt = filteredRecords
            .filter(r => String(r.Pyttype || '').toLowerCase() === 'cash')
            .reduce((sum, r) => sum + Number(r.RcdAmt || 0), 0);

        const banking = filteredRecords
            .filter(r => String(r.Pyttype || '').toLowerCase() === 'rtgs/neft')
            .reduce((sum, r) => sum + Number(r.RcdAmt || 0), 0);

        const bankAmt = filteredRecords
            .filter(r => String(r.Pyttype || '').toLowerCase() === 'bank')
            .reduce((sum, r) => sum + Number(r.RcdAmt || 0), 0);

        const totalAmt = Number(onlineAmt) + Number(cashAmt) + Number(banking) + Number(bankAmt);

        // Summary with borders
        const summaryCells = [
            { row: summaryStart, label: "Total Records:", value: totalRecords },
            { row: summaryStart + 1, label: "Online Payments:", value: onlineAmt },
            { row: summaryStart + 2, label: "Cash Payments:", value: cashAmt },
            { row: summaryStart + 3, label: 'NEFT/RTGS:', value: banking },
            { row: summaryStart + 4, label: 'Bank', value: bankAmt },
            { row: summaryStart + 5, label: 'Total Amount:', value: totalAmt },
        ];

        summaryCells.forEach((item, idx) => {
            const labelCell = worksheet.getCell(`G${item.row}`);
            const valueCell = worksheet.getCell(`H${item.row}`);

            labelCell.value = item.label;
            valueCell.value = item.value;

            labelCell.font = { bold: true };
            valueCell.font = { bold: true };

            if (idx === 1) valueCell.font.color = { argb: '008000' };   // Online
            if (idx === 2) valueCell.font.color = { argb: 'D35400' };   // Cash
            if (idx === 3) valueCell.font.color = { argb: '008080' }; // Rtgs/Neft
            if (idx === 4) valueCell.font.color = { argb: '6A0DAD' }; // Bank 

            valueCell.numFmt = '#,##0';

            // Apply light blue border to summary cells
            labelCell.border = lightBlueBorder;
            valueCell.border = lightBlueBorder;
        });

        // ==================== COLUMN WIDTHS ====================
        worksheet.columns = [
            { width: 10 },  // A Property
            { width: 14 },  // B Receipt No
            { width: 16 },  // C Adv / Type
            { width: 13 },  // D Receipt Date
            { width: 14 },  // E Mobile No
            { width: 24 },  // F Resident
            { width: 14 },  // G Payment Type
            { width: 14 },  // H Transaction Date
            { width: 16 }   // I Amount
        ];

        // ==================== EXPORT ====================
        workbook.xlsx.writeBuffer().then((buffer) => {
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const fileName = `Receipt_Report_${fromDate.format('DDMMYYYY')}_to_${toDate.format('DDMMYYYY')}.xlsx`;
            saveAs(blob, fileName);
        });
    };

    return (
        <Box sx={{
            width: '100%',
            height: '90vh',
            padding: '10px',
            backgroundColor: 'rgb(249, 249, 249)',
            position: 'relative'
        }}>
            <Box
                sx={{
                    backgroundColor: "#ffffff",
                    padding: { xs: "8px 16px", sm: "12px 16px" },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                    borderRadius: "12px",
                    margin: "10px auto",
                    maxWidth: "95%",
                    flexDirection: { xs: "column", sm: "column", md: "row" },
                    gap: 2,
                }}
            >
                <Breadcrumbs
                    aria-label="breadcrumb"
                    separator="›"
                    sx={{
                        fontSize: { xs: "14px", sm: "16px" },
                        display: "flex",
                        flexDirection: { xs: "column", sm: "column", md: "row" }
                    }}
                >
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
                        onClick={() => navigate('/dashboard')}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        <Box component="span">Home</Box>
                    </Link>
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500" }}
                        onClick={() => navigate('/reports/accountsFinance')}
                    >
                        Account & Finance
                    </Link>

                    <Typography color="text.primary" sx={{ fontWeight: "500" }}>
                        Collection Report
                    </Typography>
                </Breadcrumbs>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1}
                        sx={{
                            flexWrap: "nowrap",
                            alignItems: "center",
                            justifyContent: { xs: "flex-start", md: "flex-end" },
                            width: "auto",
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                width: { xs: '100%', md: 'auto' },
                                justifyContent: { xs: 'space-between', md: 'flex-start' },
                                flexWrap: { xs: 'nowrap', md: 'nowrap' },
                            }}
                        >
                            <DatePicker
                                label="From Date"
                                value={fromDate}
                                onChange={(newValue) => setFromDate(newValue)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: {
                                            flex: { xs: '0 1 48%', md: '0 1 160px' },
                                            bgcolor: 'white',
                                            borderRadius: 2,
                                            '& .MuiInputBase-root': { fontSize: '1rem', py: 0.2 },
                                            '& .MuiOutlinedInput-input': { padding: '4px 8px' },
                                        },
                                    },
                                }}
                                maxDate={toDate}
                                format="DD/MM/YYYY"
                            />
                            <DatePicker
                                label="To Date"
                                value={toDate}
                                onChange={(newValue) => setToDate(newValue)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: {
                                            flex: { xs: '0 1 48%', md: '0 1 160px' },
                                            bgcolor: 'white',
                                            borderRadius: 2,
                                            '& .MuiInputBase-root': { fontSize: '1rem', py: 0.2 },
                                            '& .MuiOutlinedInput-input': { padding: '4px 8px' },
                                        },
                                    },
                                }}
                                minDate={fromDate}
                                format="DD/MM/YYYY"
                            />
                        </Stack>
                        <Stack direction="row" spacing={1}
                            sx={{
                                mt: { xs: 1, md: 0 },
                                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                width: { xs: '100%', md: 'auto' },
                            }}
                        >
                            <Tooltip title='Retrieve data' arrow>
                                <Button
                                    variant="contained"
                                    onClick={handleLoadRecords}
                                    disabled={!fromDate || !toDate}
                                    sx={{
                                        height: 30,
                                        bgcolor: '#635bff',
                                        color: 'white',
                                        fontSize: '0.8rem',
                                        fontWeight: 'medium',
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        px: 2,
                                        '&:hover': {
                                            bgcolor: '#635bff',
                                        },
                                    }}
                                >
                                    Get Data
                                </Button>
                            </Tooltip>
                            <Tooltip title='Print the records' arrow>
                                <Button
                                    variant="outlined"
                                    startIcon={<PrintIcon />}
                                    onClick={handlePrint}
                                    disabled={records.length === 0}
                                    sx={{
                                        height: 30,
                                        borderColor: 'secondary.main',
                                        color: 'secondary.main',
                                        fontSize: '0.8rem',
                                        fontWeight: 'medium',
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        px: 2,
                                        '&:hover': {
                                            borderColor: 'secondary.dark',
                                            boxShadow: theme.shadows[4],
                                            transform: 'translateY(-1px)',
                                            transition: 'all 0.2s ease-in-out',
                                        },
                                    }}
                                >
                                    Print
                                </Button>
                            </Tooltip>
                            <Tooltip title='Export to excel' arrow>
                                <Button
                                    variant="outlined"
                                    startIcon={<ImportExportIcon />}
                                    onClick={handleExportToExcel}
                                    disabled={records.length === 0}
                                    sx={{
                                        height: 30,
                                        borderColor: 'success.main',
                                        color: 'success.main',
                                        fontSize: '0.8rem',
                                        fontWeight: 'medium',
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        px: 2,
                                        '&:hover': {
                                            borderColor: 'success.dark',
                                            boxShadow: theme.shadows[4],
                                            transform: 'translateY(-1px)',
                                            transition: 'all 0.2s ease-in-out',
                                        },
                                    }}
                                >
                                    Export
                                </Button>
                            </Tooltip>
                            <Tooltip title='Previous Page' arrow>
                                <IconButton sx={{ width: 32, height: 32, color: '#ffffff', bgcolor: '#635bff', ":hover": { bgcolor: '#635bff' } }}
                                    onClick={() => navigate('/reports/accountsFinance')}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title='Move to Collection Summary' arrow>
                                <IconButton sx={{ width: 32, height: 32, color: '#ffffff', bgcolor: '#16a34a', ":hover": { bgcolor: '#15803d' } }}
                                    onClick={() => navigate('/collection-summary')}
                                >
                                    <ArrowForwardIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                </LocalizationProvider>
            </Box>

            <Container maxWidth={false}
                sx={{
                    width: '95%', mt: 1, p: 2, pb: 1, bgcolor: 'grey.50', borderRadius: 4,
                    boxShadow: theme.shadows[10],
                    mx: 'auto',
                }}
            >
                <ToastContainer />
                <Box sx={{ displayPrint: 'none' }}>
                    <Box sx={{ mb: 1 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search by Resident, Mobile No, Recipt No,Trj No, Pyt Type, Amount, etc."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                    '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.8 },
                                    boxShadow: theme.shadows[1],
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.dark',
                                        boxShadow: theme.shadows[2],
                                    },
                                },
                            }}
                            sx={{ maxWidth: '100vw', mx: 'auto' }}
                        />
                    </Box>

                    <Divider sx={{ mb: 1, borderColor: 'grey.200', borderWidth: 1 }} />

                    {loading && (
                        <Box sx={{ width: '100%', mb: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                            <CircularProgress size="3rem" sx={{ mb: 1 }} />
                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                Loading data...
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* TABLE WITH FIXED HEADER & FIXED TOTAL */}
                <Box
                    sx={{
                        maxHeight: '65vh',
                        overflowY: 'auto',
                        overflowX: 'auto',
                        position: 'relative',
                        '@media print': {
                            maxHeight: 'none',
                            overflow: 'visible',
                        },
                    }}
                    ref={contentRef}
                >
                    {filteredRecords.length === 0 ? (
                        <Fade in timeout={700}>
                            <Typography
                                sx={{
                                    textAlign: 'center',
                                    color: 'text.secondary',
                                    py: 4,
                                    fontSize: '0.9rem',
                                    fontWeight: 'medium',
                                    fontStyle: 'italic',
                                }}
                            >
                                No Bill found for the selected date range or search criteria. Please adjust and try again.
                            </Typography>
                        </Fade>
                    ) : (
                        <Fade in timeout={700}>
                            <table
                                style={{
                                    width: '100%',
                                    borderCollapse: 'separate',
                                    borderSpacing: '0 4px',
                                }}
                            >
                                {/* FIXED HEADER */}
                                <thead>
                                    <tr>
                                        {[
                                            'Property', 'Rec No', 'AdvNo', 'AdvFor', 'RecDt', 'Resident',
                                            'Mobile No', 'TrjNo', 'TrjDt', 'Pyttype', 'Amount'
                                        ].map((h, i) => (
                                            <th
                                                key={i}
                                                style={{
                                                    background: '#7c75f3ff',
                                                    color: '#fff',
                                                    padding: '6px 6px',
                                                    textAlign: 'left',
                                                    fontSize: '0.85rem',
                                                    fontWeight: 700,
                                                    position: 'sticky',
                                                    top: 0,
                                                    zIndex: 11,
                                                    boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
                                                    textTransform: 'none',
                                                    letterSpacing: '0.5px',
                                                }}
                                            >
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                {/* BODY*/}
                                <tbody>
                                    {filteredRecords.map((rec, idx) => (
                                        <tr
                                            key={rec.id || idx}
                                            style={{
                                                backgroundColor: idx % 2 === 0 ? '#f9f9f9ff' : '#fff',
                                                transition: 'background 0.2s',
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e3f2fd'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#f9f9f9ff' : '#fff'}
                                        >
                                            <td style={cellStyle}>{rec.PropName}</td>
                                            <td style={cellStyle}>{rec.RecNo}</td>
                                            <td style={cellStyle}>{rec.PytAdvNo}</td>
                                            <td style={cellStyle}>{rec.Remark}</td>
                                            <td style={cellStyle}>{dayjs(rec.RecDt).format('DD-MM-YYYY')}</td>
                                            <td style={cellStyle}>{rec.ResidentName}</td>
                                            <td style={cellStyle}>{rec.MobileNo}</td>
                                            <td style={cellStyle}>{rec.ChequeNo}</td>
                                            <td style={cellStyle}>{dayjs(rec.ChqDt).format('DD-MM-YYYY')}</td>
                                            <td style={cellStyle}>{rec.Pyttype}</td>
                                            <td style={{ ...cellStyle, fontWeight: 700, color: theme.palette.secondary.main }}>
                                                ₹{Number(rec.RcdAmt).toLocaleString('en-IN')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>

                                {/* FIXED TOTAL ROW */}
                                <tfoot className="no-print">
                                    <tr>
                                        <td
                                            colSpan={6}
                                            style={{
                                                ...cellStyle,
                                                fontWeight: 700,
                                                textAlign: 'left',
                                                backgroundColor: '#e8f5e9',
                                                position: 'sticky',
                                                bottom: 0,
                                                zIndex: 10,
                                                boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
                                            }}
                                        >
                                            Total Records: {filteredRecords.length}
                                        </td>
                                        <td
                                            colSpan={6}
                                            style={{
                                                ...cellStyle,
                                                fontWeight: 800,
                                                textAlign: 'right',
                                                backgroundColor: '#e8f5e9',
                                                color: '#1b5e20',
                                                position: 'sticky',
                                                bottom: 0,
                                                zIndex: 10,
                                                boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
                                            }}
                                        >
                                            Total: ₹{total.toLocaleString('en-IN')}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </Fade>
                    )}
                </Box>
            </Container>
        </Box>
    );
};

// Compact cell style – reduced height
const cellStyle = {
    padding: '4px 6px',
    fontSize: '0.82rem',
    borderBottom: '1px solid #e0e0e0',
    whiteSpace: 'nowrap',
};

export default ReceiptReport;